<template>
  <div class="sf-el product-advise-element">
    <div v-if="label" class="product-advise-element__title">
      {{ label }}
    </div>
    <div v-if="!dummy" class="product-advise-element__content">
      <template v-if="matchedProducts.length > 0">
        <div
          v-for="product in matchedProducts"
          :key="product.product.title"
          class="product-advise-element__product"
        >
          <div
            class="product-advise-element__img"
            v-if="product.product.imageUrl && element.props.product_show_image"
          >
            <img :src="product.product.imageUrl" :alt="product.product.title" />
          </div>
          <div class="product-advise-element__info">
            <div class="product-advise-element__name">
              <a :href="product.product.url" target="_blank">{{ product.product.title }}</a>
              <span v-if="product.product.label" class="product-advise-element__label">
                {{ product.product.label }}
              </span>
            </div>
            <div class="product-advise-element__desc">{{ product.product.description }}</div>
            <div class="product-advise-element__price">
              <span v-if="product.product.salePrice" class="product-advise-element__sale">
                €{{ product.product.salePrice }}
              </span>
              <span
                v-if="product.product.price"
                :class="{ 'product-advise-element__old': product.product.salePrice }"
              >
                €{{ product.product.price }}
              </span>
            </div>
            <div
              class="product-advise-element__actions"
              v-if="element.props.product_add_to_cart || element.props.product_show_in_shop"
            >
              <a
                class="btn product-advise-element__btn"
                v-if="element.props.product_add_to_cart"
                :href="product.product.addToCartUrl"
                target="_blank"
              >
                {{ $t('form.fields.product_advise.add_to_cart') }}
              </a>
              <a
                class="btn btn--variant-outlined product-advise-element__btn"
                v-if="element.props.product_show_in_shop"
                :href="product.product.url"
                target="_blank"
              >
                {{ $t('form.fields.product_advise.show_in_shop') }}
              </a>
            </div>
          </div>
        </div>
      </template>
      <div v-else class="product-advise-element__empty">
        {{ $t('form.fields.product_advise.no_products_found') }}
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent, ref, computed, onMounted } from 'vue';
  import { getProductFeeds } from '@shared/services/productService';
  import {
    useProductAdviseMatching,
    useProductAdviseMatchingScoring,
  } from '@shared/composables/useProductAdviseMatching';
  import Btn from '@shared/components/Btn.vue';

  export default defineComponent({
    name: 'ProductAdviseElement',
    components: {
      Btn,
    },
    props: {
      label: { type: String, default: 'Recommended products' },
      element: { type: Object, required: true },
      formData: { type: Object, required: true },
      form: { type: Object, required: true },
      dummy: { type: Boolean, default: false },
    },
    setup(props) {
      if (props.dummy) {
        return {
          label: props.label || (props.element && props.element.props && props.element.props.label),
          dummy: true,
        };
      }
      const productFeed = ref([]);
      onMounted(async () => {
        const feeds = await getProductFeeds();
        const feed = feeds.find((f) => f.id === props.element.props.feedId);
        productFeed.value = feed ? feed.products : [];
      });

      const matchedProducts = computed(() => {
        // Use scoring system if available, fallback to old system
        const settings = props.element.props.matchingSettings || {};
        if (settings.useScoringSystem !== false) {
          // Use new scoring system
          return useProductAdviseMatchingScoring(
            productFeed.value,
            props.element.props.matchRules || [],
            props.formData,
            props.form,
            {
              scoreThreshold: settings.scoreThreshold || 0,
              maxProducts: settings.maxProducts || 10,
              masterRules: settings.masterRules || [],
            }
          );
        } else {
          // Fallback to old system
          return useProductAdviseMatching(
            productFeed.value,
            props.element.props.matchRules,
            props.formData,
            props.form
          );
        }
      });

      return {
        label: props.label || props.element.props.label,
        matchedProducts,
        dummy: false,
      };
    },
  });
</script>

<style scoped lang="scss">
  .product-advise-element {
    // padding: 1.5rem;
    // background: var(--cs-input-default-background, #f8f9fa);
    // border: 1px solid var(--cs-input-default-border, #dee2e6);
    // border-radius: 0.5rem;
    // margin: 1rem 0;

    &__title {
      font-size: 1rem;
      font-weight: 600;
      color: var(--cs-text-heading, #333);
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
      // border-bottom: 1px solid var(--cs-input-default-border, #dee2e6);
    }
    &__content {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
    }
    &__product {
      display: flex;
      flex-direction: row;
      gap: 1.5rem;
      align-items: flex-start;
      background: var(--cs-choice-default-background);
      border: 1px solid var(--cs-choice-default-border);
      border-radius: 0.5rem;
      padding: 1rem;
      @include lg-down {
        flex-direction: column;
        // gap: 0.5rem;
      }
    }
    &__img {
      height: 120px;
      width: 120px;
      flex-shrink: 0;
      border-radius: 0.5rem;
      overflow: hidden;
      background: var(--slate-100);
      border: 1px solid var(--slate-200);
      // @include lg-down {
      //   height: 80px;
      //   width: 80px;
      // }
      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }
    &__info {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }
    &__name {
      font-weight: 600;
      font-size: 1.05rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      a {
        color: var(--cs-text-heading, #333);
        text-decoration: none;
        &:hover {
          text-decoration: underline;
        }
      }
    }
    &__label {
      background: #f59e42;
      color: #fff;
      font-size: 0.85rem;
      border-radius: 0.25rem;
      padding: 0.1rem 0.5rem;
      margin-left: 0.5rem;
    }
    &__desc {
      color: #666;
      font-size: 0.95rem;
      margin-bottom: 0.25rem;
    }
    &__price {
      font-size: 1.1rem;
      font-weight: 500;
      color: #1a1a1a;
      .product-advise-element__sale {
        color: #d32f2f;
        font-weight: 700;
        margin-right: 0.5rem;
      }
      .product-advise-element__old {
        text-decoration: line-through;
        color: #888;
        margin-left: 0.5rem;
      }
    }
    &__actions {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      width: 100%;
    }
    &__empty {
      text-align: center;
      color: var(--cs-text-secondary, #666);
      font-style: italic;
      padding: 2rem;
    }
    &__btn {
      width: 100%;
      text-decoration: none;
    }
  }
</style>
